import useSiegeSystemRepository from "@/hooks/useSiegeSystemRepository";
import useUpgradesStore from "@/store/useUpgradesStore";
import type { UpgradeParams } from "@/types/tile";
import {
	TileEntityParam,
	type TileIndex,
	type TileNeighbour,
} from "@/types/tile";
import {
	FrameName,
	tilerepoFriendlyUnitsFrameNames,
	tilerepoRiversFrameNames,
	tilerepoSeasFrameNames,
	tilerepoSettlementEmptyFrameNames,
} from "../config";
import { TileVisibilityType } from "../config/terrain";
import { UpgradeIds } from "../config/upgrades";
import TileEntity from "../TileEntity";

type GrasslandFrameNames =
	| FrameName.GRASSLAND
	| FrameName.GRASSLAND1
	| FrameName.GRASSLAND2
	| FrameName.GRASSLAND3
	| FrameName.GRASSLAND4;

class GrasslandTerrainTileEntity extends TileEntity {
	public static readonly frameName: GrasslandFrameNames = FrameName.GRASSLAND;

	public get canUpgradeTo(): Set<FrameName> {
		if (this.cachedCanUpgradeTo) return this.cachedCanUpgradeTo;

		const upgrades = new Set<FrameName>(super.canUpgradeTo);
		if (!this.upgrades.size) return (this.cachedCanUpgradeTo = upgrades);

		const neighbours = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				0,
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				const [tileId] = this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId);
				if (!frameName) return null;

				return {
					tileIndex,
					tileId,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null &&
					this.tilerepo.getParam(
						neighbour.tileIndex,
						TileEntityParam.VISIBILITY,
					) === TileVisibilityType.EXPLORED,
			) as Array<TileNeighbour>;

		if (
			!neighbours.find(({ frameName }: TileNeighbour): boolean =>
				tilerepoSeasFrameNames.has(frameName),
			)
		) {
			upgrades.delete(FrameName.LIGHTHOUSE);
		}

		if (
			!neighbours.find(({ frameName }: TileNeighbour): boolean =>
				tilerepoRiversFrameNames.has(frameName),
			)
		) {
			upgrades.delete(FrameName.ROOF_TILE_FACTORY);
		}

		const ssr = useSiegeSystemRepository();
		if (!ssr.isUnlocked || !this.isBlockedByEnemy) {
			for (const frameName of tilerepoFriendlyUnitsFrameNames) {
				upgrades.delete(frameName);
			}
		}

		return (this.cachedCanUpgradeTo = upgrades);
	}

	public get upgrades(): Map<FrameName, UpgradeParams> {
		if (this.cachedUpgrades) return this.cachedUpgrades;

		const upgrades = new Map<FrameName, UpgradeParams>(
			super.config.upgrades,
		);

		const neighbours = this.tilerepo
			.getNeighbourTileIndexesByTileCoordinates(
				{ x: this.x, y: this.y },
				1,
			)
			.map((tileIndex: TileIndex): TileNeighbour | null => {
				const [tileId] = this.tilerepo.getTileIdsByTileIndex(tileIndex);
				if (!tileId) return null;

				const frameName = this.tilerepo.getFrameNameByTileId(tileId);
				if (!frameName) return null;
				return {
					tileIndex,
					tileId,
					frameName,
				};
			})
			.filter(
				(neighbour: TileNeighbour | null): boolean =>
					neighbour !== null &&
					tilerepoSettlementEmptyFrameNames.has(neighbour.frameName),
			) as Array<TileNeighbour>;

		if (
			!neighbours.length ||
			useUpgradesStore().getCurrentLevelById(UpgradeIds.outskirts) === 0
		) {
			upgrades.delete(FrameName.SMALL_HOUSES);
		}

		return (this.cachedUpgrades = new Map<FrameName, UpgradeParams>(
			upgrades,
		));
	}
}

export default GrasslandTerrainTileEntity;
